/**
 * localStorage persistence for the live price edition, keyed by vendor.
 *
 * The cache stores BOTH answers a successful fetch can give: an edition, or the
 * fact that this vendor has none. That distinction is load-bearing — under
 * per-vendor data gating, "no editions" (DELL) and "fetch failed" mean opposite
 * things, and a cached "none" is what lets an offline reload keep serving
 * productDb for a vendor that legitimately has no edition.
 *
 * Anything malformed degrades to a MISS. A corrupted entry must never crash the
 * app or, worse, produce a partial sheet that prices some SKUs and not others.
 */
import { SHEET_FORMAT } from './publishPlan';
import type { EditionRecord } from './publishPlan';

export type CachedState =
  | { kind: 'edition'; edition: EditionRecord }
  | { kind: 'none' };

const key = (vendor: string) => `conf106.priceEdition.${vendor}`;

function isEdition(v: unknown): v is EditionRecord {
  if (typeof v !== 'object' || v === null) return false;
  const e = v as Record<string, unknown>;
  return typeof e.id === 'string'
    && typeof e.vendor === 'string'
    && typeof e.editionNumber === 'number'
    && typeof e.asOf === 'string'
    && typeof e.publishedAt === 'string'
    && e.sheetFormat === SHEET_FORMAT
    && typeof e.sheet === 'object' && e.sheet !== null;
}

export function readCache(vendor: string): CachedState | null {
  let raw: string | null = null;
  try { raw = localStorage.getItem(key(vendor)); } catch { return null; }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const state = parsed as Record<string, unknown>;
    if (state.kind === 'none') return { kind: 'none' };
    if (state.kind === 'edition' && isEdition(state.edition)) {
      return { kind: 'edition', edition: state.edition };
    }
    return null;
  } catch { return null; }
}

export function writeCache(vendor: string, state: CachedState): void {
  // Quota and private-mode failures must not break pricing — the cache is an
  // optimisation on the read path, not the source of truth.
  try { localStorage.setItem(key(vendor), JSON.stringify(state)); } catch { /* ignore */ }
}

export function clearCache(vendor: string): void {
  try { localStorage.removeItem(key(vendor)); } catch { /* ignore */ }
}
